import { Stack, Tooltip, Typography, tooltipClasses } from "@mui/material";
import { styled } from "@mui/material/styles";
import { KeyRoles } from "../../../../types/enums";
import { keyRolesItems } from "../../../../types/genereal";
import { PersonaBubbleChartItem } from "../../../../types";
import {
  DIVIDER_COLOR,
  EXTRA_COLOR,
  GAP_COLOR,
} from "./personaGapChart.shared";

export const StatsTooltip = styled(
  ({ className, ...props }: React.ComponentProps<typeof Tooltip>) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ),
)(() => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: "none",
    padding: "16px",
    borderRadius: "8px",
    backgroundColor: "#FFFFFF",
    color: "#232323",
    fontFamily: "Inter, sans-serif",
    boxShadow: `
      0px 0px 16px -4px #0000001A,
      0px 10px 15px -3px #0000001A,
      0px -1px 4px 0px #0000000D
    `,
  },
  [`& .${tooltipClasses.arrow}`]: {
    color: "#FFFFFF",
  },
}));

export const PersonaStatsTooltipContent = ({
  persona,
  role,
}: {
  persona: PersonaBubbleChartItem;
  role: KeyRoles;
}) => {
  const roleMeta = keyRolesItems[role];
  const diff = persona.actual - persona.expected;
  const maxBase = Math.max(persona.actual, persona.expected);
  const actualPercent =
    maxBase > 0 ? Math.round((persona.actual / maxBase) * 100) : 0;
  const expectedPercent =
    maxBase > 0 ? Math.round((persona.expected / maxBase) * 100) : 0;
  const diffPercent =
    persona.expected > 0 ? Math.round((diff / persona.expected) * 100) : 0;
  const diffColor = diff < 0 ? GAP_COLOR : EXTRA_COLOR;

  return (
    <Stack
      gap="12px"
      sx={{ fontSize: "13px", lineHeight: "18px", minWidth: "260px" }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "15px",
          lineHeight: "22px",
          color: "#121212",
        }}
      >
        {persona.persona_name}
      </Typography>

      <Stack
        flexDirection="row"
        alignItems="center"
        gap="4px"
        sx={{
          alignSelf: "flex-start",
          padding: "4px 8px",
          borderRadius: "7px",
          backgroundColor: roleMeta.backgroundColor,
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 500,
            lineHeight: "16px",
            color: roleMeta.color,
          }}
        >
          {roleMeta.title}
        </Typography>
      </Stack>

      <Stack
        sx={{ height: "1px", backgroundColor: DIVIDER_COLOR, width: "100%" }}
      />

      <Stack
        sx={{
          display: "grid",
          gridTemplateColumns: "auto 1fr auto",
          columnGap: "12px",
          rowGap: "8px",
          alignItems: "baseline",
        }}
      >
        <span style={{ color: "#232323" }}>Identified</span>
        <span />
        <span
          style={{
            fontWeight: 500,
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {persona.actual} Accts ({actualPercent}%)
        </span>

        <span style={{ color: "#232323" }}>Benchmark</span>
        <span />
        <span
          style={{
            fontWeight: 500,
            fontVariantNumeric: "tabular-nums",
            textAlign: "right",
            whiteSpace: "nowrap",
          }}
        >
          {persona.expected} Accts ({expectedPercent}%)
        </span>

        {diff !== 0 && (
          <>
            <span style={{ color: diffColor }}>Gap</span>
            <span />
            <span
              style={{
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                textAlign: "right",
                whiteSpace: "nowrap",
                color: diffColor,
              }}
            >
              {diff > 0 ? `+${diff}` : diff} Accts (
              {diffPercent > 0 ? `+${diffPercent}` : diffPercent}%)
            </span>
          </>
        )}
      </Stack>
    </Stack>
  );
};
